import type { App } from 'vue';
import { sendError, sendVisitBeacon } from './reporter';

const EXTENSION_FILENAME_PATTERNS = [
  '/u.c.b.r.o.w.s.e.r/',
];

const EXTENSION_STACK_PATTERNS = [
  'injectScript',
  'findReactPropsKey',
  'injectJsCode',
  'swkkInit',
  'SwkkJq',
  'ucbrowser_script',
];

function isExtensionInjectedError(errorStack: string): boolean {
  return EXTENSION_STACK_PATTERNS.some(pattern => errorStack.includes(pattern));
}

export function initErrorHandler(app: App) {
  // Vue 组件错误（全平台可用，但仅在 H5 端初始化时注册）
  app.config.errorHandler = (err, _instance, info) => {
    sendError({
      errorMessage: err instanceof Error ? err.message : String(err),
      errorStack: err instanceof Error ? err.stack : '',
      errorType: 'Vue组件错误',
      additionalData: { lifecycleHook: info },
    });
  };

  // #ifdef H5
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const { reason } = event;
    const errorStack
      = reason?.stack || (reason instanceof Error ? reason.stack : '');

    if (errorStack && isExtensionInjectedError(errorStack)) return;

    const errorMsg
      = reason?.message
        || (typeof reason === 'string' ? reason : JSON.stringify(reason));
    sendError({
      errorMessage: errorMsg,
      errorStack: errorStack || '',
      errorType: '未捕获的Promise异常',
    });
  });

  window.addEventListener(
    'error',
    (event: ErrorEvent | Event) => {
      if (event instanceof ErrorEvent) {
        if (event.message === 'Script error.' && !event.filename && !event.lineno)
          return;

        if (event.message?.includes('ResizeObserver loop')) return;

        const filename = event.filename || '';
        if (
          filename.startsWith('chrome-extension://')
          || filename.startsWith('moz-extension://')
          || filename.startsWith('safari-web-extension://')
        ) {
          return;
        }

        if (EXTENSION_FILENAME_PATTERNS.some(p => filename.includes(p))) return;

        const errorStack = (event.error as Error)?.stack || '';
        if (isExtensionInjectedError(errorStack)) return;

        sendError({
          errorMessage: event.message,
          lineNumber: event.lineno,
          columnNumber: event.colno,
          errorStack,
          errorType: '代码错误',
          additionalData: { filename },
        });
      }
      else if (event.target) {
        const { localName, currentSrc } = event.target as any;
        if (localName === 'img') return;
        const resourceUrl = currentSrc || '';
        if (!resourceUrl) return;
        try {
          if (new URL(resourceUrl).hostname !== window.location.hostname)
            return;
        }
        catch {
          return;
        }
        sendError({
          errorMessage: `${localName}加载错误`,
          errorType: '资源错误',
          errorStack: '',
          additionalData: { resourceUrl },
        });
      }
    },
    true,
  );

  // 页面卸载时通过 sendBeacon 上报最终访问记录
  window.addEventListener('pagehide', () => {
    sendVisitBeacon({});
  });
  // #endif
}
